import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ArrowLeft, Sparkles, GraduationCap, Star } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAID_TIERS = [
  'founder_monthly', 'pastor_pro', 'founder_lifetime', 'seminary_annual',
  // legacy
  'ministry_plus', 'church_team', 'pro',
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { headers: { ...getAuthHeaders() } });
      if (response.ok) setUser(await response.json());
    } catch {}
    finally { setCheckingAuth(false); }
  };

  const handleUpgrade = async (plan) => {
    if (!user) {
      navigate('/login', { state: { returnUrl: '/pricing' } });
      return;
    }
    if (PAID_TIERS.includes(user.subscription_tier)) {
      toast.info('You already have an active plan. Contact support to change it.');
      return;
    }
    setLoadingPlan(plan);
    try {
      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ origin_url: window.location.origin, plan })
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to start checkout');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const isPaid = user && PAID_TIERS.includes(user.subscription_tier);
  const tier = user?.subscription_tier;

  const isCurrent = (plan) => {
    if (!tier) return false;
    const map = {
      pastor_pro_monthly: ['pastor_pro'],
      pastor_pro_annual:  ['pastor_pro'],
      seminary_annual:   ['seminary_annual'],
    };
    return (map[plan] || [plan]).includes(tier);
  };

  const PlanButton = ({ plan, label, className = 'btn-secondary' }) => {
    if (!user) {
      return (
        <Link to="/signup" className="block w-full">
          <Button className={`w-full ${className}`}>{label}</Button>
        </Link>
      );
    }
    if (isCurrent(plan)) {
      return <Button className="w-full btn-secondary" disabled>Current Plan</Button>;
    }
    if (isPaid) {
      return <Button className="w-full btn-secondary" disabled>Active Plan</Button>;
    }
    return (
      <Button
        className={`w-full ${className}`}
        onClick={() => handleUpgrade(plan)}
        disabled={loadingPlan === plan}
      >
        {loadingPlan === plan ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Processing...
          </span>
        ) : label}
      </Button>
    );
  };

  const Feature = ({ text }) => (
    <li className="flex items-start gap-2 text-stone-600 text-sm">
      <Check className="w-4 h-4 text-[#0ea5e9] flex-shrink-0 mt-0.5" strokeWidth={2} />
      {text}
    </li>
  );

  const paidFeatures = [
    'Unlimited sermon analyses',
    'Clean exports — no watermark',
    'Compare up to 3 sermons',
    'Shareable analysis links',
    'Monday Trending Sermons email',
    'Find Related Sermons',
    'Google Docs & Notion export',
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]" data-testid="pricing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-stone-600 hover:text-[#1e3a8a]">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Back</span>
            </Link>
            <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</Link>
            <div className="hidden sm:flex items-center gap-6">
              <Link to="/about" className="text-sm text-stone-500 hover:text-[#1e3a8a] transition-colors">About</Link>
              <Link to="/blog" className="text-sm text-stone-500 hover:text-[#1e3a8a] transition-colors">Blog</Link>
            </div>
            <div className="w-24 text-right">
              {user ? (
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-[#1e3a8a]">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button variant="ghost" className="text-[#1e3a8a]">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">

          {/* Page heading */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-4">
              Plans for every ministry
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Start free. Upgrade when you need more. All paid plans include unlimited analyses, clean exports, and no hidden fees.
            </p>
          </div>

          {/* ── FREE + PASTOR PRO PLANS ─────────────────────────── */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">

            {/* Free */}
            <div className="pricing-card flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-stone-800 mb-1">Free</h3>
                <p className="text-stone-500 text-sm">Try SermonSift with no commitment</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-[#1e3a8a]">$0</span>
                <span className="text-stone-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  '3 sermon analyses per month',
                  'Watermarked exports only',
                  'AI-powered summaries',
                  'Scripture reference detection',
                  'Key timestamps & quotes',
                  'Application points',
                ].map((f, i) => <Feature key={i} text={f} />)}
              </ul>
              {!user ? (
                <Link to="/signup" className="block">
                  <Button className="btn-secondary w-full" data-testid="free-signup-btn">Get Started Free</Button>
                </Link>
              ) : tier === 'free' ? (
                <Button className="btn-secondary w-full" disabled>Current Plan</Button>
              ) : null}
            </div>

            {/* Pastor Pro Monthly */}
            <div className="pricing-card flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-stone-800 mb-1 flex items-center gap-2">
                  Pastor Pro
                  <Sparkles className="w-4 h-4 text-amber-500" />
                </h3>
                <p className="text-stone-500 text-sm">For individual pastors and preachers</p>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold text-[#1e3a8a]">$9.99</span>
                <span className="text-stone-500">/month</span>
              </div>
              <p className="text-xs text-stone-400 mb-6">Or save with the annual plan below →</p>
              <ul className="space-y-3 mb-8 flex-1">
                {paidFeatures.map((f, i) => <Feature key={i} text={f} />)}
              </ul>
              <PlanButton plan="pastor_pro_monthly" label="Start Monthly" className="btn-secondary" />
            </div>

            {/* Pastor Pro Annual — BEST VALUE */}
            <div className="pricing-card featured flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1e3a8a] text-white text-xs font-semibold px-4 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                <Star className="w-3 h-3" /> Best Value
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-stone-800 mb-1 flex items-center gap-2">
                  Pastor Pro Annual
                  <Sparkles className="w-4 h-4 text-amber-500" />
                </h3>
                <p className="text-stone-500 text-sm">All of Pastor Pro, billed annually</p>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold text-[#1e3a8a]">$79</span>
                <span className="text-stone-500">/year</span>
              </div>
              <p className="text-xs text-emerald-600 font-medium mb-6">Save $40.88 vs monthly · Just $6.58/month</p>
              <ul className="space-y-3 mb-8 flex-1">
                {[...paidFeatures, 'Locked-in annual rate'].map((f, i) => <Feature key={i} text={f} />)}
              </ul>
              <PlanButton plan="pastor_pro_annual" label="Get Pastor Pro Annual" className="btn-primary" />
            </div>
          </div>

          {/* ── SEMINARY ANNUAL ───────────────────────────────────── */}
          <div className="grid md:grid-cols-1 gap-6 mb-20">
            <div className="pricing-card flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-5 h-5 text-[#1e3a8a]" strokeWidth={1.5} />
                  <h3 className="text-xl font-semibold text-stone-800">Seminary Annual</h3>
                </div>
                <p className="text-stone-500 text-sm mb-4">Student-friendly annual pricing for theology students</p>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {paidFeatures.map((f, i) => <Feature key={i} text={f} />)}
                </ul>
              </div>
              <div className="flex-shrink-0 text-center md:text-right">
                <div className="mb-1">
                  <span className="text-4xl font-bold text-[#1e3a8a]">$49</span>
                  <span className="text-stone-500">/year</span>
                </div>
                <p className="text-xs text-emerald-600 font-medium mb-4">Just $4.08/month · student pricing</p>
                <div className="w-full md:w-52">
                  <PlanButton plan="seminary_annual" label="Get Seminary Plan" className="btn-secondary" />
                </div>
              </div>
            </div>
          </div>

          {/* ── FAQ ──────────────────────────────────────────────── */}
          <div className="max-w-3xl mx-auto">
            <h2 className="font-serif text-2xl font-bold text-[#1e3a8a] text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-5">
              {[
                {
                  q: 'What counts as one analysis?',
                  a: 'Each time you submit a YouTube URL or paste a transcript for AI processing, that counts as one analysis. Viewing previously saved analyses never counts against your limit.'
                },
                {
                  q: 'When does the free plan reset?',
                  a: 'Free plan usage resets on the 1st of every calendar month, not on a rolling 30-day basis.'
                },
                {
                  q: 'Can I cancel a paid plan at any time?',
                  a: 'Yes. You can cancel any monthly or annual subscription at any time. You will retain access until the end of your current billing period.'
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'All major credit and debit cards are accepted through Stripe. We do not store card details.'
                },
              ].map((faq, i) => (
                <div key={i} className="card p-6">
                  <h3 className="font-semibold text-stone-800 mb-2">{faq.q}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* CTA */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to transform your sermon study?
          </h2>
          <p className="text-blue-200 mb-8">
            Join pastors and ministry leaders saving hours every week with SermonSift.
          </p>
          <Link to="/signup">
            <Button className="bg-white text-[#1e3a8a] hover:bg-stone-100 rounded-full px-8 py-3 font-medium shadow-lg">
              Start Your Free Account
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#fafaf9] border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <span className="font-serif text-lg font-bold text-[#1e3a8a]">SermonSift</span>
              <p className="text-sm text-stone-400 mt-1">A Bethany Labs product</p>
            </div>
            <div className="flex items-center gap-6 text-sm text-stone-500">
              <Link to="/" className="hover:text-[#1e3a8a]">Home</Link>
              <span>•</span>
              <span>© {new Date().getFullYear()} Bethany Labs. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
